/* ---------------------------------------------------------------------------
   MY BAG — cart page behaviour

   Quantity, remove and move-to-wish-list all go through the Cart AJAX API and
   repaint from the response rather than reloading. A reload on every press of
   the + button is the easy version and it feels broken.

   The wish list is the same localStorage list the rest of the site uses --
   "synergy:wishlist" -- so an item moved here shows up in the wish list panel
   immediately, with no server round trip to go wrong.
   --------------------------------------------------------------------------- */
(function () {
  const root = document.querySelector('[data-syn-cart-page]');
  if (!root) return;

  const WISHLIST_KEY = 'synergy:wishlist';
  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents, window.__synMoneyFormat)
      : '$' + (cents / 100).toFixed(2);

  function readWishlist() {
    try { return JSON.parse(window.localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveToWishlist(handle) {
    if (!handle) return;
    try {
      const items = readWishlist();
      if (!items.includes(handle)) {
        items.push(handle);
        window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
        /* Let the wish list panel and its counter know without a reload. */
        document.dispatchEvent(new CustomEvent('synergy:wishlist:changed', { detail: { handle } }));
      }
    } catch (e) { /* private mode: the cart action still completes */ }
  }

  /* ---------- talking to the cart ---------- */

  let inFlight = false;

  /* Addressed by line item key, never by position.

     Position is not stable: the bag panel, another tab, or a size swap (which
     removes and re-adds, sending the line to the end) all renumber the cart
     without this page knowing. A page left open then sends "remove line 1" and
     Shopify obediently removes whatever is at position 1 now -- a different
     product from the one whose button was pressed. The key identifies the line
     itself, so a stale key simply fails instead of hitting an innocent one. */
  async function changeLine(key, quantity) {
    if (inFlight || !key) return null;
    inFlight = true;
    root.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    } finally {
      inFlight = false;
      root.removeAttribute('aria-busy');
    }
  }

  /* ---------- repaint ---------- */

  function paint(cart) {
    if (!cart) return;

    /* An emptied bag changes the whole page, not just a number. */
    if (cart.item_count === 0) { window.location.reload(); return; }

    /* Match each row to its own cart line by key. Matching by index meant that
       removing the first of three handed row 1 the second item's total and
       deleted the last row -- the wrong card vanished and the survivors showed
       figures belonging to other products. */
    const byKey = new Map(cart.items.map((item) => [item.key, item]));
    const rows = [...root.querySelectorAll('[data-syn-line]')];
    for (const row of rows) {
      const item = byKey.get(row.dataset.key);
      if (!item) { row.remove(); continue; }
      const input = row.querySelector('[data-syn-qty-input]');
      if (input) input.value = item.quantity;
      const lineTotal = row.querySelector('.syn-cartpage__line-total-value');
      if (lineTotal) lineTotal.textContent = money(item.original_line_price);
    }

    /* A line in the cart that this page never rendered means the bag was
       changed somewhere else. The page cannot invent the missing card, so it
       reloads rather than showing a total that does not match what is listed. */
    const shown = new Set(rows.filter((r) => r.isConnected).map((r) => r.dataset.key));
    if (cart.items.some((item) => !shown.has(item.key))) { window.location.reload(); return; }

    const label = cart.item_count === 1 ? 'item' : 'items';
    root.querySelectorAll('[data-syn-cartpage-count]').forEach((el) => {
      el.textContent = cart.item_count + ' ' + label;
    });
    const subtotal = root.querySelector('.syn-cartpage__summary-row .syn-cartpage__summary-value');
    if (subtotal) subtotal.textContent = money(cart.items_subtotal_price);
    const totals = root.querySelectorAll('.syn-cartpage__summary-row--total .syn-cartpage__summary-total');
    if (totals.length > 1) totals[totals.length - 1].textContent = money(cart.total_price);
    const summaryLabel = root.querySelector('.syn-cartpage__summary-row .syn-cartpage__summary-label');
    if (summaryLabel) summaryLabel.textContent = cart.item_count + ' ' + label;

    /* The header bag badge lives outside this section. */
    document.dispatchEvent(new CustomEvent('synergy:cart:updated', { detail: { cart } }));
  }

  function keyOf(el) {
    const row = el.closest('[data-syn-line]');
    return row ? row.dataset.key : '';
  }

  /* ---------- events ---------- */

  root.addEventListener('click', async (event) => {
    const up = event.target.closest('[data-syn-qty-up]');
    const down = event.target.closest('[data-syn-qty-down]');
    const remove = event.target.closest('[data-syn-cart-remove]');
    const wish = event.target.closest('[data-syn-cart-wish]');
    if (!up && !down && !remove && !wish) return;

    event.preventDefault();

    if (up || down) {
      const row = (up || down).closest('[data-syn-line]');
      const input = row.querySelector('[data-syn-qty-input]');
      const next = Math.max(0, Number(input.value || 0) + (up ? 1 : -1));
      input.value = next;
      paint(await changeLine(keyOf(up || down), next));
      return;
    }

    if (remove) {
      paint(await changeLine(keyOf(remove), 0));
      return;
    }

    if (wish) {
      /* Save first: if the cart call fails the item is still on the list, which
         is the harmless direction to fail in. */
      saveToWishlist(wish.dataset.handle);
      wish.classList.add('is-saved');
      paint(await changeLine(keyOf(wish), 0));
    }
  });

  /* ---------- changing size in the bag ----------
     Shopify has no "swap the variant on this line" call, so it is a removal
     followed by an add. Both have to succeed: if the new size turns out to be
     sold out the original is put back rather than leaving the shopper with an
     empty line they did not ask for.

     The page reloads afterwards on purpose. Changing size changes the price,
     the line total, the image and the description all at once, and the line
     moves to the end of the bag -- repainting that in place would be more
     ways to be wrong than it is worth for something done once. */
  async function swapSize(select) {
    const key = select.dataset.key;
    const previous = select.dataset.current;
    const chosen = select.value;
    if (!key || !chosen || chosen === previous) return;

    const quantity = Math.max(1, Number(select.dataset.qty || 1));
    select.disabled = true;

    async function post(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      return res.ok ? res.json() : null;
    }

    const removed = await post('/cart/change.js', { id: key, quantity: 0 });
    if (!removed) { select.disabled = false; select.value = previous; return; }

    const added = await post('/cart/add.js', { id: Number(chosen), quantity: quantity });
    if (!added) {
      await post('/cart/add.js', { id: Number(previous), quantity: quantity });
      select.disabled = false;
      select.value = previous;
      return;
    }
    window.location.reload();
  }

  /* Typing a quantity directly. */
  root.addEventListener('change', async (event) => {
    const size = event.target.closest('[data-syn-size-select]');
    if (size) { await swapSize(size); return; }

    const input = event.target.closest('[data-syn-qty-input]');
    if (!input) return;
    const next = Math.max(0, Number(input.value || 0));
    input.value = next;
    paint(await changeLine(keyOf(input), next));
  });

  /* ---------- promo code ----------
     A storefront cannot apply a code directly; /discount/CODE sets it on the
     session and redirects back. Shopify gives no signal either way, so the
     verdict has to be worked out here: remember the code across the redirect,
     then on return compare it against the discounts the cart actually has. */
  const promoInput = root.querySelector('[data-syn-promo-input]');
  const promoApply = root.querySelector('[data-syn-promo-apply]');
  const promoMessage = root.querySelector('[data-syn-promo-message]');
  const PENDING = 'synergy:promo:pending';

  function appliedCodes() {
    const tag = root.querySelector('[data-syn-applied-discounts]');
    if (!tag) return [];
    try { return (JSON.parse(tag.textContent) || []).map((t) => String(t).trim().toUpperCase()); }
    catch (e) { return []; }
  }

  function say(text, ok) {
    if (!promoMessage || !text) return;
    promoMessage.textContent = text;
    promoMessage.hidden = false;
    promoMessage.classList.toggle('is-ok', !!ok);
    promoMessage.classList.toggle('is-bad', !ok);
  }

  function applyPromo() {
    const code = ((promoInput && promoInput.value) || '').trim();
    if (!code) { if (promoInput) promoInput.focus(); return; }
    if (appliedCodes().includes(code.toUpperCase())) {
      say((promoMessage && promoMessage.dataset.ok) || 'Code applied.', true);
      return;
    }
    try { window.sessionStorage.setItem(PENDING, code); } catch (e) {}
    if (promoApply) promoApply.disabled = true;
    window.location.href = '/discount/' + encodeURIComponent(code)
      + '?redirect=' + encodeURIComponent(window.location.pathname);
  }

  if (promoApply) promoApply.addEventListener('click', applyPromo);
  if (promoInput) promoInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); applyPromo(); }
  });

  /* Back from the redirect: did the code we tried actually land? */
  (function judgePendingCode() {
    if (!promoMessage) return;
    let pending = null;
    try { pending = window.sessionStorage.getItem(PENDING); } catch (e) { return; }
    if (!pending) return;
    try { window.sessionStorage.removeItem(PENDING); } catch (e) {}

    const landed = appliedCodes().includes(pending.trim().toUpperCase());
    say(landed ? promoMessage.dataset.ok : promoMessage.dataset.bad, landed);
    if (!landed && promoInput) promoInput.value = pending;
  })();


  /* The order note posts on its own so it is not lost when the shopper leaves
     without pressing Update. */
  const note = root.querySelector('#syn-cart-note');
  if (note) {
    let timer;
    note.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        }).catch(() => {});
      }, 600);
    });
  }
})();
